/**
 *
 * Data sources
 *
 */

import React, { memo, useEffect } from 'react';

import { AccountType } from '@ariksa/cloud-account';
import { CloudProviders } from '@ariksa/cloud-account/api';
import { Box, Stack, Center } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import {
  ActionButton,
  EditIcon,
  DeleteActionButton,
  renderTime,
  WithResourceIcon,
  RediscoverIcon,
  StackedCell,
} from 'components/index';
import { renderStatus } from 'containers/Setup/CloudAccounts/utils';
import { RediscoverConfirmationModal } from 'containers/Setup/Integrations/Components/Utils/RediscoverConfirmationModal';
import { useIntegrations } from 'containers/Setup/Integrations/hooks/useIntegrations';
import { selectIntegrations } from 'containers/Setup/Integrations/selectors';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';
import { selectSharedState } from 'containers/SharedState/selectors';

export const DataSources = memo(() => {
  const { account, rediscoverAccount } = useSelector(selectIntegrations);
  const { accounts } = useSelector(selectSetup);
  const { snapshotStatus } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const {
    onEditProvider,
    onConfirmDeleteAccount,
    onClickRediscoverInventory,
    isOpenRediscoverModal,
    getAccounts,
    ...rest
  } = useIntegrations();

  useEffect(() => {
    getAccounts(AccountType.Saas);
  }, [getAccounts]);

  const columns = [
    {
      header: <Box pl={16}>Name</Box>,
      accessor: 'name',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.cloud_type}
          iconFilled={false}
          iconSize="regular"
          spacing={10}
        >
          <Box>{row?.name ?? row?.name}</Box>
        </WithResourceIcon>
      ),
    },
    {
      header: 'Authentication',
      align: 'left',
      render: ({ row }) => {
        const val =
          row?.cloud_type === CloudProviders.Gitlab ||
          row?.cloud_type === CloudProviders.GitlabIac ||
          row?.cloud_type === CloudProviders.Github ||
          row?.cloud_type === CloudProviders.GithubIac ||
          row?.cloud_type === CloudProviders.Bitbucket ||
          row?.cloud_type === CloudProviders.BitbucketIac
            ? 'Access Token'
            : row?.snowflake?.login;
        return (
          <CustomTooltip label={val}>
            <Box>{val}</Box>
          </CustomTooltip>
        );
      },
    },
    {
      header: 'Account',
      align: 'left',
      render: ({ row }) => {
        const val = row?.snowflake?.account ?? row?.cloud_account_id;
        return <CustomTooltip label={val}>{val}</CustomTooltip>;
      },
    },
    {
      header: 'Created',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={renderTime({
            value: row?.created_date ?? row?.created_date,
          })}
          lower={row?.created_by ?? row?.created_by}
          showUpperTooltip={false}
        />
      ),
    },
    {
      header: 'Status',
      accessor: 'status.code',
      align: 'center',
      render: renderStatus,
    },
    {
      header: 'Last Sync',
      render: ({ row }) =>
        renderTime({ value: snapshotStatus.data?.[row.uuid]?.created_at }),
      align: 'left',
    },
    {
      header: 'Actions',
      accessor: 'actions',
      align: 'center',
      render: ({ row }) => {
        return (
          <Center>
            <ActionButton
              label={'Rediscover'}
              icon={<RediscoverIcon />}
              onClick={() => onClickRediscoverInventory(row)}
            />
            <ActionButton
              label={'Edit'}
              icon={<EditIcon />}
              onClick={() => {
                onEditProvider(row);
              }}
            />
            <DeleteActionButton
              type={row?.cloud_type}
              name={row?.name}
              onConfirm={() =>
                onConfirmDeleteAccount(row?.uuid, AccountType.Saas)
              }
            />
          </Center>
        );
      },
    },
  ];

  return (
    <Stack spacing={4} h="full">
      <>
        <Table
          columns={columns}
          isLoading={
            accounts.isLoading || account.isLoading || snapshotStatus.isLoading
          }
          data={accounts.data}
          pagination={{
            totalCount: accounts.page.totalCount,
            pageInfo: accounts.page.info,
            onChange: pageInfo => {
              dispatch(setupActions.updateAccountsPageInfo(pageInfo));
            },
          }}
        />
        {isOpenRediscoverModal && (
          <RediscoverConfirmationModal
            accountType={AccountType.Saas}
            isLoading={rediscoverAccount.isLoading}
            isOpenRediscoverModal={isOpenRediscoverModal}
            {...rest}
          />
        )}
      </>
    </Stack>
  );
});
